import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { useBrandContext } from '../App';
import { useNotifications } from './NotificationSystem';

const ContentPipeline = () => {
  const { brands } = useBrandContext();
  const { showSuccess, showError, showConfirm } = useNotifications();
  const [selectedCard, setSelectedCard] = useState(null);
  const [showAddItem, setShowAddItem] = useState(false);
  const [filterBrand, setFilterBrand] = useState('all');
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [newItem, setNewItem] = useState({
    brand: '',
    platform: 'IG',
    title: '',
    description: '',
    priority: 'Medium',
    assignedTo: '',
    dueDate: '',
    tags: '',
    contentType: 'Post',
    estimatedTime: '30 min',
    stage: 'Concept'
  });

  // Simplified content data structure
  const [contentItems, setContentItems] = useState([
    { id: 1, brand: 'CELSIUS', platform: 'IG', title: 'Morning Workout', description: 'Pre-workout routine showcase', priority: 'High', assignedTo: 'John', dueDate: '2024-12-15', tags: 'fitness,morning', contentType: 'Carousel', estimatedTime: '45 min', stage: 'Concept' },
    { id: 2, brand: 'PBR', platform: 'X', title: 'Game Day Special', description: 'Super bowl promotion', priority: 'Medium', assignedTo: 'Sarah', dueDate: '2024-12-20', tags: 'sports,limited', contentType: 'Post', estimatedTime: '30 min', stage: 'Concept' },
    { id: 3, brand: 'PBR', platform: 'X', title: 'Weekend Plans', description: 'Casual weekend content', priority: 'Low', assignedTo: 'Mike', dueDate: '2024-12-18', tags: 'lifestyle,casual', contentType: 'Thread', estimatedTime: '60 min', stage: 'Draft Copy' },
    { id: 4, brand: '7-Eleven', platform: 'IG', title: 'Coffee Blend', description: 'New coffee series launch', priority: 'High', assignedTo: 'Emma', dueDate: '2024-12-14', tags: 'product,launch', contentType: 'Reel', estimatedTime: '90 min', stage: 'Draft Creative' },
    { id: 5, brand: 'Aperol', platform: 'FB', title: 'Summer Cocktail', description: 'Seasonal cocktail recipes', priority: 'Medium', assignedTo: 'Lisa', dueDate: '2024-12-16', tags: 'recipe,summer', contentType: 'Album', estimatedTime: '30 min', stage: 'Internal Review' },
    { id: 6, brand: 'CHOMPS', platform: 'TikTok', title: 'Hiking Snacks', description: 'Outdoor adventure content', priority: 'High', assignedTo: 'Tom', dueDate: '2024-12-17', tags: 'outdoor,fitness', contentType: 'Video', estimatedTime: '120 min', stage: 'Client Review' },
    { id: 8, brand: 'CELSIUS', platform: 'TikTok', title: 'Pre-workout Routine', description: 'Exercise routine demo', priority: 'Medium', assignedTo: 'John', dueDate: '2024-12-19', tags: 'fitness,education', contentType: 'Video', estimatedTime: '60 min', stage: 'Approved' },
    { id: 9, brand: 'OLIPOP', platform: 'IG', title: 'Digestive Tips', description: 'Health tips series', priority: 'Low', assignedTo: 'Sarah', dueDate: '2024-12-21', tags: 'health,tips', contentType: 'Carousel', estimatedTime: '45 min', stage: 'Scheduled' }
  ]);

  const pipelineStages = [
    'Concept', 'Draft Copy', 'Draft Creative', 'Internal Review', 'Client Review', 'Approved', 'Scheduled'
  ];

  const platforms = ['IG', 'X', 'FB', 'TikTok', 'LinkedIn', 'YouTube'];
  const priorities = ['Low', 'Medium', 'High', 'Urgent'];
  const contentTypes = ['Post', 'Carousel', 'Reel', 'Story', 'Video', 'Thread', 'Album'];
  const timeEstimates = ['15 min', '30 min', '45 min', '1 hour', '2 hours', '3 hours'];

  // Update newItem.brand when brands change
  useEffect(() => {
    if (brands.length > 0) {
      setNewItem(prev => ({
        ...prev,
        brand: brands.includes(prev.brand) ? prev.brand : brands[0]
      }));
      setFilterBrand(prev => prev === 'all' || brands.includes(prev) ? prev : 'all');
    }
  }, [brands]);

  // Get items for a specific stage
  const getItemsForStage = useCallback((stage) => {
    let items = contentItems.filter(item => item.stage === stage);
    if (filterBrand !== 'all') {
      items = items.filter(item => item.brand === filterBrand);
    }
    return items;
  }, [contentItems, filterBrand]);

  // Add new item
  const addNewItem = () => {
    if (!newItem.brand.trim() || !newItem.title.trim()) {
      showError('Validation Error', 'Please fill in all required fields (Brand and Title)');
      return;
    }

    const newId = Math.max(...contentItems.map(item => item.id), 0) + 1;
    const itemToAdd = {
      id: newId,
      brand: newItem.brand.trim(),
      platform: newItem.platform,
      title: newItem.title.trim(),
      description: newItem.description.trim(),
      priority: newItem.priority,
      assignedTo: newItem.assignedTo.trim(),
      dueDate: newItem.dueDate,
      tags: newItem.tags.trim(),
      contentType: newItem.contentType,
      estimatedTime: newItem.estimatedTime,
      stage: newItem.stage
    };

    setContentItems(prev => [...prev, itemToAdd]);

    // Reset form
    setNewItem({
      brand: brands[0] || '7-Eleven',
      platform: 'IG',
      title: '',
      description: '',
      priority: 'Medium',
      assignedTo: '',
      dueDate: '',
      tags: '',
      contentType: 'Post',
      estimatedTime: '30 min',
      stage: 'Concept'
    });
    setShowAddItem(false);
    showSuccess('Item Added', `"${itemToAdd.title}" has been created successfully`);
    console.log('✅ Item added successfully:', itemToAdd);
  };

  // ELEGANT DELETE FUNCTION WITH CUSTOM CONFIRMATION
  const deleteItem = useCallback((itemId, event) => {
    console.log('🚀 DELETE FUNCTION CALLED:', { itemId, hasEvent: !!event });

    // ABSOLUTE EVENT ISOLATION
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      if (event.nativeEvent) {
        event.nativeEvent.preventDefault();
        event.nativeEvent.stopPropagation();
        if (event.nativeEvent.stopImmediatePropagation) {
          event.nativeEvent.stopImmediatePropagation();
        }
      }
    }

    // FIND AND VALIDATE ITEM
    const itemToDelete = contentItems.find(item => item.id === itemId);
    if (!itemToDelete) {
      showError('Delete Error', 'Item not found in database');
      console.error('❌ CRITICAL: Item not found:', itemId);
      console.error('Available items:', contentItems.map(item => ({ id: item.id, title: item.title })));
      return false;
    }

    console.log('📋 Item to delete confirmed:', itemToDelete);

    // SHOW CUSTOM CONFIRMATION DIALOG
    showConfirm(
      'Delete Content Item',
      `Are you sure you want to delete "${itemToDelete.title}" from ${itemToDelete.brand}? This action cannot be undone.`,
      () => confirmDelete(itemId, itemToDelete.title),
      () => console.log('❌ User cancelled deletion')
    );
    return true;
  }, [contentItems, showConfirm, showError]);

  // CONFIRM DELETE EXECUTION
  const confirmDelete = useCallback((itemId, itemTitle) => {
    console.log('🔥 Executing confirmed deletion for:', { itemId, itemTitle });

    // ATOMIC DELETION WITH IMMEDIATE FEEDBACK
    let deletionSuccessful = false;
    setContentItems(prevItems => {
      const newItems = prevItems.filter(item => item.id !== itemId);
      const wasDeleted = newItems.length < prevItems.length;
      console.log('📊 Deletion result:', { before: prevItems.length, after: newItems.length, success: wasDeleted, deletedId: itemId });
      deletionSuccessful = wasDeleted;
      return newItems;
    });

    // CLEANUP MODAL IF NEEDED
    if (selectedCard && selectedCard.id === itemId) {
      console.log('🔒 Closing modal for deleted item');
      setSelectedCard(null);
    }

    // FINAL VERIFICATION
    setTimeout(() => {
      const currentItems = contentItems; // This captures the state after update
      const itemStillExists = currentItems.some(item => item.id === itemId);
      if (itemStillExists) {
        console.error('🚨 CRITICAL FAILURE: Item still exists after deletion attempt!');
        console.error('Current items:', currentItems.map(item => ({ id: item.id, title: item.title })));
        showError('Delete Failed', 'Item still exists. Please refresh and try again.');
      } else {
        console.log('✅ SUCCESS: Item permanently deleted:', itemId);
        showSuccess('Item Deleted', `"${itemTitle}" has been removed successfully`);
      }
    }, 100);

    return deletionSuccessful;
  }, [selectedCard, contentItems, showSuccess, showError]);

  // Move item between stages
  const moveItem = useCallback((itemId, newStage) => {
    setContentItems(prev => prev.map(item => item.id === itemId ? { ...item, stage: newStage } : item));
    setSelectedCard(null);
  }, []);

  // Drag and drop handlers
  const handleDragStart = (e, item) => {
    e.dataTransfer.setData('itemId', item.id.toString());
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetStage) => {
    e.preventDefault();
    const itemId = parseInt(e.dataTransfer.getData('itemId'));
    moveItem(itemId, targetStage);
  };

  // Add item to a specific stage
  const addItemToStage = (stage) => {
    setNewItem({ ...newItem, stage: stage });
    setShowAddItem(true);
  };

  // Reset form
  const resetForm = () => {
    setNewItem({
      brand: brands[0] || '7-Eleven',
      platform: 'IG',
      title: '',
      description: '',
      priority: 'Medium',
      assignedTo: '',
      dueDate: '',
      tags: '',
      contentType: 'Post',
      estimatedTime: '30 min',
      stage: newItem.stage
    });
    setShowAddItem(false);
  };

  // Content Card with elegant delete
  const ContentCard = React.memo(({ item, index }) => {
    // ISOLATED DELETE HANDLER
    const handleDeleteClick = useCallback((e) => {
      console.log('🖱️ DELETE BUTTON CLICKED:', { itemId: item.id, itemTitle: item.title });

      // IMMEDIATE EVENT PREVENTION
      if (e) {
        e.preventDefault();
        e.stopPropagation();
        if (e.nativeEvent) {
          e.nativeEvent.preventDefault();
          e.nativeEvent.stopPropagation();
          if (e.nativeEvent.stopImmediatePropagation) {
            e.nativeEvent.stopImmediatePropagation();
          }
        }
      }

      // DIRECT DELETE CALL (shows custom dialog)
      deleteItem(item.id, e);
    }, [item.id, item.title, deleteItem]);

    // ISOLATED CARD CLICK HANDLER
    const handleCardClick = useCallback((e) => {
      // PREVENT CARD CLICK IF DELETE BUTTON WAS CLICKED
      if (e.target.closest('.delete-button') || e.target.closest('[data-delete="true"]')) {
        console.log('🚫 Ignoring card click - delete button was clicked');
        return;
      }

      console.log('🃏 CARD CLICKED (OPENING MODAL):', { itemId: item.id, itemTitle: item.title });
      setSelectedCard(item);
    }, [item]);

    return (
      <motion.div
        key={item.id}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ delay: index * 0.05 }}
        drag
        onDragStart={(e) => handleDragStart(e, item)}
        whileDrag={{ scale: 1.05, rotate: 2, zIndex: 50 }}
        className="relative group"
      >
        <div className="bg-white/80 backdrop-blur-sm p-3 md:p-4 rounded-xl shadow-apple cursor-pointer hover:shadow-apple-hover hover:scale-[1.02] border border-white/50 transition-all duration-200">
          {/* ELEGANT DELETE BUTTON */}
          <button
            onClick={handleDeleteClick}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            data-delete="true"
            className="delete-button absolute top-2 right-2 w-7 h-7 md:w-8 md:h-8 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 hover:bg-red-600 z-50 shadow-lg hover:scale-110 touch-target"
            title={`Delete ${item.title}`}
            type="button"
          >
            <span className="text-white font-bold text-sm md:text-base">×</span>
          </button>

          {/* CARD CONTENT AREA */}
          <div onClick={handleCardClick} className="pr-8 md:pr-10 cursor-pointer" data-card-content="true">
            <div className="flex justify-between items-start mb-2 md:mb-3">
              <span className="text-xs font-bold gradient-gold bg-clip-text text-transparent uppercase tracking-wider">
                {item.brand}
              </span>
              <span className="text-xs text-brown/40 font-bold">
                {item.platform}
              </span>
            </div>
            <p className="text-xs md:text-sm font-bold text-brown mb-2 md:mb-3 line-clamp-2 mobile-truncate-2">{item.title}</p>
            <div className="flex items-center justify-between">
              <div className="flex -space-x-1">
                <div className="w-4 h-4 md:w-5 md:h-5 rounded-full gradient-brown border-2 border-white shadow-sm"></div>
              </div>
              <span className="text-[10px] md:text-xs text-brown/60 bg-beige/50 px-2 py-1 rounded-full border border-gold/10">
                {item.dueDate || 'No date'}
              </span>
            </div>

            {/* Drag indicator */}
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-20 transition-opacity pointer-events-none">
              <span className="text-brown text-xl md:text-2xl">⋮⋮</span>
            </div>
          </div>
        </div>
      </motion.div>
    );
  });

  // Add item form
  const AddItemForm = () => (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: 'auto', opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      className="mb-4 md:mb-6 p-4 md:p-6 glass-card rounded-xl md:rounded-2xl border border-white/30"
    >
      <h3 className="text-lg font-bold text-brown mb-4">Create New Content Item</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
        {/* Brand Selection */}
        <div>
          <label className="block text-xs font-bold gradient-gold bg-clip-text text-transparent uppercase mb-2">
            Brand *
          </label>
          <select
            value={newItem.brand}
            onChange={(e) => setNewItem({ ...newItem, brand: e.target.value })}
            className="apple-input text-brown w-full text-sm"
          >
            <option value="">Select Brand</option>
            {brands.map(brand => (
              <option key={brand} value={brand}>{brand}</option>
            ))}
          </select>
        </div>

        {/* Platform Selection */}
        <div>
          <label className="block text-xs font-bold gradient-gold bg-clip-text text-transparent uppercase mb-2">
            Platform
          </label>
          <select
            value={newItem.platform}
            onChange={(e) => setNewItem({ ...newItem, platform: e.target.value })}
            className="apple-input text-brown w-full text-sm"
          >
            {platforms.map(platform => (
              <option key={platform} value={platform}>{platform}</option>
            ))}
          </select>
        </div>

        {/* Title */}
        <div>
          <label className="block text-xs font-bold gradient-gold bg-clip-text text-transparent uppercase mb-2">
            Content Title *
          </label>
          <input
            type="text"
            value={newItem.title}
            onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
            placeholder="Enter content title..."
            className="apple-input text-brown w-full text-sm"
          />
        </div>

        {/* Description */}
        <div className="md:col-span-2">
          <label className="block text-xs font-bold gradient-gold bg-clip-text text-transparent uppercase mb-2">
            Description
          </label>
          <input
            type="text"
            value={newItem.description}
            onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
            placeholder="Brief description of the content..."
            className="apple-input text-brown w-full text-sm"
          />
        </div>

        {/* Priority */}
        <div>
          <label className="block text-xs font-bold gradient-gold bg-clip-text text-transparent uppercase mb-2">
            Priority
          </label>
          <select
            value={newItem.priority}
            onChange={(e) => setNewItem({ ...newItem, priority: e.target.value })}
            className="apple-input text-brown w-full text-sm"
          >
            {priorities.map(priority => (
              <option key={priority} value={priority}>{priority}</option>
            ))}
          </select>
        </div>

        {/* Assigned To */}
        <div>
          <label className="block text-xs font-bold gradient-gold bg-clip-text text-transparent uppercase mb-2">
            Assigned To
          </label>
          <input
            type="text"
            value={newItem.assignedTo}
            onChange={(e) => setNewItem({ ...newItem, assignedTo: e.target.value })}
            placeholder="Team member name..."
            className="apple-input text-brown w-full text-sm"
          />
        </div>

        {/* Due Date */}
        <div>
          <label className="block text-xs font-bold gradient-gold bg-clip-text text-transparent uppercase mb-2">
            Due Date
          </label>
          <input
            type="date"
            value={newItem.dueDate}
            onChange={(e) => setNewItem({ ...newItem, dueDate: e.target.value })}
            className="apple-input text-brown w-full text-sm"
          />
        </div>

        {/* Content Type */}
        <div>
          <label className="block text-xs font-bold gradient-gold bg-clip-text text-transparent uppercase mb-2">
            Content Type
          </label>
          <select
            value={newItem.contentType}
            onChange={(e) => setNewItem({ ...newItem, contentType: e.target.value })}
            className="apple-input text-brown w-full text-sm"
          >
            {contentTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        {/* Estimated Time */}
        <div>
          <label className="block text-xs font-bold gradient-gold bg-clip-text text-transparent uppercase mb-2">
            Est. Time
          </label>
          <select
            value={newItem.estimatedTime}
            onChange={(e) => setNewItem({ ...newItem, estimatedTime: e.target.value })}
            className="apple-input text-brown w-full text-sm"
          >
            {timeEstimates.map(time => (
              <option key={time} value={time}>{time}</option>
            ))}
          </select>
        </div>

        {/* Tags */}
        <div className="md:col-span-3">
          <label className="block text-xs font-bold gradient-gold bg-clip-text text-transparent uppercase mb-2">
            Tags
          </label>
          <input
            type="text"
            value={newItem.tags}
            onChange={(e) => setNewItem({ ...newItem, tags: e.target.value })}
            placeholder="Enter tags separated by commas..."
            className="apple-input text-brown w-full text-sm"
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col md:flex-row gap-3 mt-4 md:mt-6">
        <motion.button
          onClick={addNewItem}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          disabled={!newItem.brand.trim() || !newItem.title.trim()}
          className={`flex-1 px-4 py-3 rounded-xl font-medium transition-colors text-sm md:text-base touch-target ${
            newItem.brand.trim() && newItem.title.trim()
              ? 'bg-apache text-white hover:bg-apache/80'
              : 'bg-brown/20 text-brown/50 cursor-not-allowed'
          }`}
        >
          <SafeIcon name="Plus" className="w-4 h-4 mr-2" />
          Create Content Item
        </motion.button>
        <motion.button
          onClick={resetForm}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="px-4 py-3 bg-brown/20 text-brown rounded-xl font-medium hover:bg-brown/30 transition-colors touch-target text-sm md:text-base"
        >
          Cancel
        </motion.button>
      </div>
    </motion.div>
  );

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-[calc(100vh-100px)] overflow-hidden flex flex-col">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 md:mb-6 px-4 gap-4">
        <h1 className="section-title mb-0 text-2xl md:text-3xl lg:text-4xl">Content Pipeline</h1>
        
        {/* Brand Filter and Add Button */}
        <div className="flex flex-col md:flex-row items-center gap-3 w-full md:w-auto">
          <select
            value={filterBrand}
            onChange={(e) => setFilterBrand(e.target.value)}
            className="apple-input text-brown text-sm w-full md:w-auto"
          >
            <option value="all">All Brands</option>
            {brands.map(brand => (
              <option key={brand} value={brand}>{brand}</option>
            ))}
          </select>
          <motion.button
            onClick={() => setShowAddItem(!showAddItem)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="apple-button flex items-center justify-center w-full md:w-auto touch-target"
          >
            <SafeIcon name="Plus" className="w-4 h-4 mr-2" />
            Add Item
          </motion.button>
        </div>
      </div>

      {/* Add Item Form */}
      <AnimatePresence>
        {showAddItem && <AddItemForm />}
      </AnimatePresence>

      <div className="flex-1 overflow-x-auto pb-4 md:pb-6">
        <div className="flex space-x-3 md:space-x-6 h-full min-w-max px-2 md:px-2">
          {pipelineStages.map((stage, stageIndex) => (
            <motion.div
              key={stage}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: stageIndex * 0.1 }}
              className="w-64 md:w-80 glass-card rounded-xl md:rounded-2xl flex flex-col border border-white/30"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, stage)}
            >
              <div className="p-3 md:p-4 border-b border-white/20 bg-gradient-to-r from-white/50 to-beige/30 rounded-t-xl md:rounded-t-2xl flex justify-between items-center">
                <h3 className="font-bold text-xs md:text-sm text-brown uppercase tracking-wide">{stage}</h3>
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="bg-gold/20 text-brown text-xs px-2 py-1 rounded-full font-bold"
                >
                  {getItemsForStage(stage).length}
                </motion.span>
              </div>
              <div className="p-2 md:p-3 flex-1 overflow-y-auto space-y-2 md:space-y-3">
                <AnimatePresence>
                  {getItemsForStage(stage).map((item, itemIndex) => (
                    <ContentCard
                      key={item.id}
                      item={item}
                      index={itemIndex}
                    />
                  ))}
                </AnimatePresence>

                {/* Add Item Button */}
                <motion.button
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => addItemToStage(stage)}
                  className="w-full py-2 md:py-3 border-2 border-dashed border-gold/30 rounded-lg md:rounded-xl text-gold text-xs font-bold hover:border-gold hover:text-brown hover:bg-gold/10 transition-all group touch-target"
                >
                  <SafeIcon name="Plus" className="w-3 h-3 inline mr-2 group-hover:rotate-90 transition-transform" />
                  Add Item
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Detail Modal with elegant delete */}
      <AnimatePresence>
        {selectedCard && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4 bg-brown/20 backdrop-blur-sm">
            <motion.div
              layoutId={`card-${selectedCard.id}`}
              className="bg-white/90 backdrop-blur-xl w-full max-w-md mobile-modal rounded-xl md:rounded-2xl shadow-apple-hover overflow-hidden border border-white/50"
            >
              <div className="h-24 md:h-32 gradient-brown p-4 md:p-6 flex justify-end items-start">
                <motion.button
                  onClick={() => setSelectedCard(null)}
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  className="text-white/80 hover:text-white transition-colors touch-target"
                >
                  <span className="text-xl md:text-2xl">×</span>
                </motion.button>
              </div>
              <div className="p-4 md:p-6 -mt-8 md:-mt-10">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white/80 backdrop-blur-sm rounded-xl md:rounded-2xl shadow-apple p-3 md:p-4 mb-4 md:mb-6 border border-white/50"
                >
                  <div className="flex items-center justify-between mb-2 md:mb-3">
                    <h2 className="text-lg md:text-xl font-bold text-brown mobile-truncate-2">{selectedCard.title}</h2>
                    <span className="px-2 md:px-3 py-1 bg-gold/20 text-brown text-xs font-bold rounded uppercase">
                      {selectedCard.brand}
                    </span>
                  </div>
                  <div className="flex items-center text-brown/60 text-xs md:text-sm mb-2">
                    <span className="font-bold text-gold mr-2">{selectedCard.platform}</span>
                  </div>
                  {selectedCard.description && (
                    <p className="text-brown/80 text-xs md:text-sm">{selectedCard.description}</p>
                  )}
                </motion.div>

                {/* Additional Details */}
                <div className="space-y-3 md:space-y-4">
                  {(selectedCard.priority || selectedCard.assignedTo || selectedCard.contentType || selectedCard.estimatedTime) && (
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="bg-beige/50 p-3 md:p-4 rounded-xl border border-gold/10"
                    >
                      <h4 className="text-xs font-bold gradient-gold bg-clip-text text-transparent uppercase mb-2 md:mb-3">Details</h4>
                      <div className="space-y-1 md:space-y-2 text-xs md:text-sm">
                        {selectedCard.priority && (
                          <div className="flex justify-between">
                            <span className="text-brown/60">Priority:</span>
                            <span className="font-medium text-brown">{selectedCard.priority}</span>
                          </div>
                        )}
                        {selectedCard.assignedTo && (
                          <div className="flex justify-between">
                            <span className="text-brown/60">Assigned:</span>
                            <span className="font-medium text-brown">{selectedCard.assignedTo}</span>
                          </div>
                        )}
                        {selectedCard.contentType && (
                          <div className="flex justify-between">
                            <span className="text-brown/60">Type:</span>
                            <span className="font-medium text-brown">{selectedCard.contentType}</span>
                          </div>
                        )}
                        {selectedCard.estimatedTime && (
                          <div className="flex justify-between">
                            <span className="text-brown/60">Est. Time:</span>
                            <span className="font-medium text-brown">{selectedCard.estimatedTime}</span>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}

                  {/* Stage Navigation */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex justify-between items-center bg-white/50 p-3 md:p-4 rounded-xl border border-white/30"
                  >
                    {pipelineStages.map((stage, idx) => {
                      const currentIndex = pipelineStages.indexOf(selectedCard.stage);
                      return (
                        <motion.button
                          key={stage}
                          onClick={() => moveItem(selectedCard.id, stage)}
                          whileHover={{ scale: idx === currentIndex ? 1 : 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          className={`w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all border touch-target ${
                            idx === currentIndex
                              ? 'gradient-gold text-white shadow-apple'
                              : idx < currentIndex
                                ? 'bg-apache text-white border-apache/30'
                                : 'bg-beige text-brown/40 border-brown/20'
                          }`}
                          disabled={idx === currentIndex}
                        >
                          {idx + 1}
                        </motion.button>
                      );
                    })}
                  </motion.div>

                  {/* ELEGANT DELETE BUTTON IN MODAL */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={(e) => {
                      console.log('🔥 MODAL DELETE BUTTON CLICKED:', { itemId: selectedCard.id, itemTitle: selectedCard.title });

                      // ABSOLUTE EVENT ISOLATION
                      if (e) {
                        e.preventDefault();
                        e.stopPropagation();
                        if (e.nativeEvent) {
                          e.nativeEvent.preventDefault();
                          e.nativeEvent.stopPropagation();
                          if (e.nativeEvent.stopImmediatePropagation) {
                            e.nativeEvent.stopImmediatePropagation();
                          }
                        }
                      }

                      deleteItem(selectedCard.id, e);
                    }}
                    data-delete="true"
                    className="delete-button w-full py-2.5 md:py-3 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition-colors flex items-center justify-center touch-target"
                    type="button"
                  >
                    <span className="text-lg md:text-xl mr-2">×</span>
                    Delete Content Item
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      const currentIndex = pipelineStages.indexOf(selectedCard.stage);
                      const nextIndex = currentIndex + 1;
                      if (nextIndex < pipelineStages.length) {
                        moveItem(selectedCard.id, pipelineStages[nextIndex]);
                      }
                    }}
                    className="w-full py-2.5 md:py-3 apple-button flex items-center justify-center touch-target"
                  >
                    <SafeIcon name="ArrowRight" className="w-4 h-4 mr-2" />
                    Move to Next Stage
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ContentPipeline;